<template>
  <b-skeleton-wrapper :loading="isLoading" class="my-2">
    <template #loading>
      <b-skeleton-img no-aspect :height="options.chart.height + 'px'"  />
    </template>
    <apexchart :height="options.chart.height" :options="options" :series="series"></apexchart>
  </b-skeleton-wrapper>
</template>
<script>
import {get, omit, sumBy, takeRight} from 'lodash'
import PAGES_TRAFFIC_QUERY from '@/graphql/PagesTrafficQuery.gql'
import APPROVED_TAGS_QUERY from '@/graphql/ApprovedTagsQuery.gql'
import Delta from '@/components/Delta'

export default {
  name: 'PagesTraffic',
  components: {Delta},
  props: {
    details: Boolean,
    site: Object,
    type: String,
    search: String,
    tag: Object,
    page:  {
      type: Object,
      default: () => ({})
    },
    allowForecast: Boolean,
    data: Array,
    height: {
      type: Number,
      default: 200
    },
    description: String,
  },

  data: function() {
    return {
      pages_traffic: [],
      tags: [],
      site_improvements: [],
    }
  },

  apollo: {
    pages_traffic: {
      query: PAGES_TRAFFIC_QUERY,
      variables() {
        return {
          site_id: this.site.id,
          filter: this.type,
          search: this.search,
          tag_id: get(this, 'activeTag.id'),
          limit: this.activeCompare.days * 2,
          page_id: this.page.id
        }
      },
      skip() {
        return !this.site.id || !!this.data
      },
      result(res) {
        if(!res.stale) {
          this.$emit('data', this.pages_traffic)
        }
      }
    },
    tags: {
      query: APPROVED_TAGS_QUERY,
      variables() { 
        return { site_id: this.$route.params.site_id }
      },
      skip() {
        return !!this.page.id
      }
    },
  },
  created() {
    if(!!this.data) {
      this.pages_traffic = this.data
    }
  },

  computed: {
    approvedTraffic() {
      return this.pages_traffic
        .filter(e => !(e.approved_clicks === null))
        .map(e => ({
          date: e.date,
          clicks: e.approved_clicks,
        }))
    },
    compareTypes() {
      const data = [
        {
          value: 'month',
          text: '4 Weeks over 4 Weeks',
          days: 7 * 4,
        },
        {
          value: 'week',
          text: 'Week over Week',
          days: 7,
        },
        {
          value: 'quarter',
          text: '3 Months over 3 Months',
          days: 7 * 4 * 3,
        },
      ]
      if(this.allowForecast) {
        data.unshift({
          value: 'forecast',
          text: 'Forecast vs. Actual',
          days: 45,
        })
      }
      return data
    },
    series() {
      if(!this.pages_traffic) {
          return []
      }
      const days = takeRight(this.pages_traffic, this.activeCompare.days)
      return [{
        name: 'Clicks',
        data: days.map(e => ({
          x: e.date,
          y: e.clicks
        }))
      }, {
        name: `Site Average`,
        data: days.map(e => ({
          x: e.date,
          y: e.estimated_clicks || 0
        }))
      }]
    },
    options() {
      const vue = this
      let title = {}
      return {
        title,
        chart: {
          height: this.height,
          type: 'line',
          zoom: {
            enabled: false
          },
          animations: {
            enabled: false
          },
          toolbar: {
            show: false,
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: 'straight',
          width: 3,
          dashArray: [0, 3, 3],
        },
        xaxis: {
          type: 'datetime',
          tooltip: {
            enabled: false
          },
          crosshairs: {
            show: false,
          }
        },
        yaxis: {
          labels: {
            formatter: function(val, data) {
              return vue.$options.filters.formatNumber(val)
            }
          }
        },
        legend: {
          show: false
        },
        colors: ['var(--chart1)', 'var(--chart2)', 'var(--info)'],
        annotations: {
          xaxis: this.pages_traffic
            .filter(e => e.annotations)
            .map(day => ({
              x: day.date.getTime(),
              text: day.annotations
                .filter(a => a.count > 0)
                .map(a => `${a.count} ${a.type.slice(0, -1)}`)
                .join(', ') + ' improvement' + (sumBy(day.annotations, 'count') > 1 ? 's' : ''),
            }))
            .concat([
                ['gsc_oldest_date', 'Search Console Property Verified'],
                ['dns_created_at', 'Domain Registered']
              ].map(([key, text]) => {
                if (!this.site[key]) { return }
                return {
                  x: new Date(this.site[key]).getTime(),
                  text
                }
              })
              .filter(e => e)
            )
            .map(e => ({
              x: e.x,
              borderColor: '#00b3bc',
              label: {
                text: e.text,
                borderColor: '#00b3bc',
                orientation: 'horizontal',
              }
            }))
        },
      }
    },
    isLoading() {
      return !this.site.id || (this.$apollo.queries.pages_traffic.loading && !this.pages_traffic.length)
    },
    activeTag() {
      if(!this.$route.query.tag) {
        return {}
      }
      return this.tags.find(e => e.id == this.$route.query.tag)
    },
    activeCompare() {
      return this.compareTypes.find(e => e.value == this.$route.query.compare) || this.compareTypes[0]
    },
  },
  methods: {
    setTag(tag) {
      if(tag == this.activeTag) {
        this.$router.replace({query: omit(this.$route.query, 'tag')})
      } else {
        const query = {...this.$route.query, tag: tag.id}
        this.$router.replace({query})
      }
    },
    setCompare(e) {
      const query = {...this.$route.query, compare: e}
      this.$router.replace({query})
    },
    delta(trend) {
      const clicks = sumBy(trend, 'clicks')
      return this.$options.filters.delta(clicks, forecast)
    },
  }
}
</script>
